// Downloads the recording's transcript export (GET /recordings/{id}/transcript/export) as a
// txt/vtt/md file via a client-constructed Blob + temporary <a download> — the API client
// only exposes the raw response text (see @scribe/core's exportTranscript), so this is where
// the MIME type / filename / download trigger live.
import { useState } from 'react';
import { TRANSCRIPT_EXPORT_CONTENT_TYPES, type TranscriptExportFormat } from '@scribe/core/types';
import { useExportTranscript } from '../lib/queries';

const FORMATS: { value: TranscriptExportFormat; label: string }[] = [
  { value: 'txt', label: 'Plain text (.txt)' },
  { value: 'vtt', label: 'Captions (.vtt)' },
  { value: 'md', label: 'Markdown (.md)' },
];

function slugify(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'transcript';
}

export function ExportMenu({
  recordingTitle,
  recordingId,
  transcriptId,
}: {
  recordingTitle: string;
  recordingId: string;
  /** TVER-3: exports the viewed version; omit for the authoritative version. */
  transcriptId?: string | undefined;
}) {
  const exportTranscript = useExportTranscript(recordingId, transcriptId);
  const [open, setOpen] = useState(false);

  function download(format: TranscriptExportFormat) {
    exportTranscript.mutate(format, {
      onSuccess: ({ text }) => {
        const blob = new Blob([text], { type: TRANSCRIPT_EXPORT_CONTENT_TYPES[format] });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${slugify(recordingTitle)}.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setOpen(false);
      },
    });
  }

  return (
    <div className="export-menu">
      <button className="ghost sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        Export
      </button>
      {open && (
        <div className="export-menu-options" role="menu">
          {FORMATS.map((f) => (
            <button
              key={f.value}
              type="button"
              className="ghost sm export-menu-option"
              role="menuitem"
              disabled={exportTranscript.isPending}
              onClick={() => download(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
      {exportTranscript.isError && (
        <p className="error">{(exportTranscript.error as Error).message || 'Export failed.'}</p>
      )}
    </div>
  );
}
